import React from 'react';
import { Link } from 'react-router-dom';
import { PRICING_PLANS, ROUTES } from '../constants';

const Pricing: React.FC = () => {
  return (
    <div className="bg-white min-h-screen">
      {/* 헤더 */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-block mb-4">
            <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
              Pricing Plans
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            합리적인 가격, 명확한 가치
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            프로젝트 규모에 맞는 최적의 플랜을 선택하세요
          </p>
        </div>
      </div>

      {/* 요금제 카드 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {PRICING_PLANS.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-xl p-8 transition-all duration-300 hover:shadow-xl ${
                plan.popular
                  ? 'border-2 border-blue-600 shadow-lg'
                  : 'border-2 border-gray-200 hover:border-blue-300'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span className="bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-bold">
                    인기
                  </span>
                </div>
              )}

              <div className="text-center mb-8 mt-2">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {plan.name}
                </h3>
                <p className="text-gray-600 text-sm mb-6">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                  {plan.period && <span className="text-gray-600 text-lg">{plan.period}</span>}
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <div className="flex-shrink-0 p-1 bg-green-100 rounded-full mr-3">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={ROUTES.SIGNUP}
                className={`block w-full text-center px-6 py-4 rounded-lg font-semibold transition-colors ${
                  plan.popular
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
                FAQ
              </span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              자주 묻는 질문
            </h2>
            <p className="text-gray-600 text-lg">궁금하신 사항을 확인해보세요</p>
          </div>
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-blue-300 transition-colors">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    플랜을 언제든지 변경할 수 있나요?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    네, 언제든지 플랜을 업그레이드하거나 다운그레이드할 수 있습니다.
                    변경 사항은 다음 결제 주기부터 적용됩니다.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-blue-300 transition-colors">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    API 호출량을 초과하면 어떻게 되나요?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    초과 사용량에 대해서는 별도로 과금됩니다. 호출량이 80%에 도달하면
                    알림을 보내드리니 미리 대비하실 수 있습니다.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-blue-300 transition-colors">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    환불 정책은 어떻게 되나요?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    유료 플랜은 7일 이내 전액 환불이 가능합니다.
                    단, 이미 사용한 API 호출량에 대해서는 차감 후 환불됩니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-blue-600 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6">
            <span className="bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold">
              Contact Us
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            아직 고민 중이신가요?
          </h2>
          <p className="text-xl mb-10 text-blue-100 max-w-2xl mx-auto">
            전문가와 상담하여 최적의 플랜을 찾아보세요
          </p>
          <Link
            to={ROUTES.SUPPORT.CONTACT}
            className="inline-flex items-center gap-3 bg-white text-blue-700 px-10 py-5 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            문의하기
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
